import { ColDef, ICellRendererParams } from 'ag-grid-community';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getVersionTypeLabel, permissionLevelEnum } from '../../../../constants';
import { useUpdateVersionNoteMutation } from '../../../../hooks/mutations/useVersionNoteMutation';
import { useVersionNotesQuery } from '../../../../hooks/queries/useVersionNotesQuery';
import { useAuth } from '../../../../hooks/useAuth';
import { VersionNote } from '../../../../services/apiSLTP/versionNotes';
import { capitalizeFirstLetter } from '../../../../utils/capitalize';
import IconLine from '../../../ui/IconLine';
//import UpdateVersionNoteModal from '../../../modals/admin/UpdateVersionNoteModal';
import { createDateColumn, createDateTimeColumn } from '../../../../utils/tables/commonColumns';
import UpdateVersionNoteModal from '../../../modals/admin/configuration/UpdateVersionNoteModal';
import IconButton from '../../../ui/IconButton';
import GenericAgGridTable from '../../GenericAgGridTable';

const VersionNotesSettingsRow: React.FC<{}> = ({}) => {
    const { t } = useTranslation();
    const { user } = useAuth();

    const [selectedVersionNote, setSelectedVersionNote] = useState<VersionNote | null>(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);

    const { data: dataVersions, isLoading, refetch } = useVersionNotesQuery({});

    const { mutate } = useUpdateVersionNoteMutation({
        onSuccess: () => refetch(),
    });

    const handleEdit = (entity: VersionNote) => {
        setSelectedVersionNote(entity);
        setIsEditMode(true);
        setShowEditModal(true);
    };

    const handleDelete = (entity: VersionNote) => {
        setSelectedVersionNote(entity);
        setShowDeleteModal(true);
    };

    const columnDefs: ColDef<VersionNote>[] = [
        {
            headerName: capitalizeFirstLetter(t('name')),
            field: 'versionName',
            initialFlex: 1,
            minWidth: 400,
            colId: 'name',
        },

        {
            headerName: capitalizeFirstLetter(t('actions')),
            cellRenderer: (params: ICellRendererParams<VersionNote>) => {
                if (!params.data) return null;
                return (
                    <IconLine>
                        <IconButton
                            iconName="edit"
                            title={capitalizeFirstLetter(t('modifyVersionNote'))}
                            onClick={() => {
                                if (!params.data) return;
                                handleEdit(params.data);
                            }}
                        />
                        {/*user && user?.profileId.permsLevel <= permissionLevelEnum.ADMIN && (
                            <IconButton
                                iconName="delete"
                                title={capitalizeFirstLetter(t('deleteVersionNote'))}
                                onClick={() => {
                                    if (!params.data) return;
                                    handleDelete(params.data);
                                }}
                                disabled={params.data.crudStatus === crudStatusEnum.DELETED}
                            />
                        )*/}
                    </IconLine>
                );
            },
            colId: 'actions',
            filter: false,
            enableRowGroup: false,
            enableValue: false,
            sortable: false,
        },
        /* {
            headerName: capitalizeFirstLetter(t('status')),
            valueGetter: (params) => capitalizeFirstLetter(t(getCrudStatusLabel(params.data?.crudStatus))),
            cellRenderer: (params: ICellRendererParams<VersionNote>) => {
                if (!params.data) return params.value;
                return <StateCell stateColor={getCrudStatusColor(params.data?.crudStatus)} text={t(getCrudStatusLabel(params.data?.crudStatus))} />;
            },
            colId: 'status',
        },*/
        {
            headerName: capitalizeFirstLetter(t('version')),
            field: 'version',
            colId: 'version',
        },
        createDateColumn<VersionNote>('date', 'date'),
        {
            headerName: capitalizeFirstLetter(t('newFeatures')),
            field: 'newFeatures',
            colId: 'newFeatures',
            cellStyle: { whiteSpace: 'pre-wrap' },
            filter: 'agTextColumnFilter',
            initialWidth: 400,
        },
        {
            headerName: capitalizeFirstLetter(t('upgrades')),
            field: 'upgrades',
            colId: 'upgrades',
            cellStyle: { whiteSpace: 'pre-wrap' },
            filter: 'agTextColumnFilter',
            initialWidth: 400,
        },
        {
            headerName: capitalizeFirstLetter(t('bugsCorrections')),
            field: 'bugsCorrections',
            colId: 'bugsCorrections',
            cellStyle: { whiteSpace: 'pre-wrap' },
            filter: 'agTextColumnFilter',
            initialWidth: 400,
        },
        {
            headerName: capitalizeFirstLetter(t('secuirtyUpdates')),
            field: 'security',
            colId: 'security',
            cellStyle: { whiteSpace: 'pre-wrap' },
            filter: 'agTextColumnFilter',
            initialWidth: 400,
        },
        {
            headerName: capitalizeFirstLetter(t('type')),
            valueGetter: (params) => capitalizeFirstLetter(t(getVersionTypeLabel(params.data?.type))),
            colId: 'type',
        },
        createDateTimeColumn<VersionNote>('createdAt', 'createdAt'),
        createDateTimeColumn<VersionNote>('updatedAt', 'updatedAt'),
    ];

    if (user?.profileId.permsLevel === permissionLevelEnum.ADMIN) {
        columnDefs.push({
            headerName: capitalizeFirstLetter(t('_id')),
            field: '_id',
            initialWidth: 250,
            cellClass: 'usage-col-main',
            headerClass: 'usage-col-main',
            colId: '_id',
        });
    }

    return (
        <>
            <GenericAgGridTable
                columnDefs={columnDefs}
                rowData={dataVersions?.data.sort((a, b) => b.date.localeCompare(a.date)) || []}
                loading={isLoading}
                headerRightComponent={
                    <IconButton
                        iconName="addCircle"
                        onClick={() => {
                            setSelectedVersionNote(null);
                            setIsEditMode(false);
                            setShowEditModal(true);
                        }}
                        title={capitalizeFirstLetter(t('addVersionNote'))}
                    />
                }
                exportCsvFileName="admin-versions"
                agGridProps={{ getRowId: ({ data }) => data._id }}
            />

            {showEditModal && (
                <UpdateVersionNoteModal
                    showEditModal={showEditModal}
                    setShowEditModal={setShowEditModal}
                    versionNote={selectedVersionNote}
                    isEditMode={isEditMode}
                    refetch={refetch}
                />
            )}

            {/*showDeleteModal && selectedVersionNote && (
                <DeleteModal
                    open={showDeleteModal}
                    handleClose={() => setShowDeleteModal(false)}
                    deleteAction={() => {
                        mutate({ entityId: selectedVersionNote._id, data: { crudStatus: crudStatusEnum.DELETED } });
                    }}
                    title={t('entity')}
                    itemName={selectedVersionNote.version}
                />
            )*/}
        </>
    );
};

export default VersionNotesSettingsRow;
